<template>
  <div class="username-selector amp-mask" ref="usernameSelector">
    <a-text as="h6" :id="title ? 'username-selector-title' : ''">{{
        title
      }}
    </a-text>
    <a-tabs
      v-if="showTabs && !simplify"
      class="username-tab-bar"
      :selected="selectedTab"
      :aria-labelledby="title ? 'username-selector-title' : 'sign-in-title'"
      :tabs="tabs"
      @switchTabs="onSwitchTab"
    />

    <div
      v-if="selectedTab === 'phone' && !simplify"
      id="phone-tabpanel"
      class="phone-container"
    >
      <reactive-phone-input
        v-model="username"
        :inputWidth="inputWidth"
        required
        :readonly="readOnly"
        :label-text="$t('signUp.guardiansOnly')"
        @update:modelValue="onUsernameChange($event)"
      />
    </div>
    <div v-if="selectedTab === 'email'" id="email-tabpanel">
      <a-form-input
        id="username"
        v-model.trim="username"
        :label="$t(`shared.${simplify ? 'emailOrPhone':'email'}`)"
        :aria-label="$t(`shared.${simplify ? 'emailOrPhone':'email'}`)"
        :placeholder="$t(`shared.placeholders.${simplify ? 'typeEmailOrPhone':'typeEmail'}`)"
        :inputWidth="inputWidth"
        :readonly="readOnly"
        :show-error="v$.username.$error"
        :error-message="simplify ? $t('signUp.errors.invalidLogin') : $t('signUp.errors.invalidEmail')"
        required
        @update:modelValue="onUsernameChange($event)"
        @invalid.prevent="showValidationMessage = true"
        @keydown.enter="enterEvent"
        :icon-left="!isPossibleEmail && simplify && username ? 'null': ''"
      >
        <template #iconLeft>
          <div v-if="!isPossibleEmail && simplify && username" class="icon-left">
            <img :src="src" alt="us flag">
            <a-text>+1</a-text>
          </div>
        </template>
      </a-form-input>
      <a-form-input
        v-if="confirm"
        id="email-confirm"
        class="email-confirm"
        :inputWidth="inputWidth"
        v-model.trim="username_confirm"
        :label="$t('shared.confirmEmail')"
        :show-error="showValidationMessage && !username_confirm.length"
        :error-message="$t('signUp.errors.emailBlank')"
        required
        :placeholder="$t('shared.placeholders.confirmEmail')"
        @update:modelValue="onUsernameConfirmChange($event)"
        @invalid.prevent="showValidationMessage = true"
      />
    </div>
  </div>
</template>

<script>
import ReactivePhoneInput from '../ReactivePhoneInput.vue';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { useVuelidate } from '@vuelidate/core';
import { required, email, or } from '@vuelidate/validators';

export default {
  name: 'UsernameSelector',
  components: {
    ReactivePhoneInput,
  },
  setup() {
    const v$ = useVuelidate();
    return {
      v$,
    };
  },
  props: {
    title: {
      type: String,
      default: 'Select a username',
    },
    showTabs: {
      type: Boolean,
      required: false,
      default: true,
    },
    confirm: {
      type: Boolean,
      required: false,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    inputWidth: {
      type: String,
      default: '200px',
    },
    simplify: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      tabs: [
        {
          label: 'Email',
          value: 'email',
          ariaControls: 'email-tabpanel',
        },
        {
          label: 'Phone',
          value: 'phone',
          ariaControls: 'phone-tabpanel',
        },
      ],
      selectedTab: 'email',
      usernameType: null,
      username: '',
      username_confirm: '',
      showValidationMessage: false,
      numberInput: null,
    };
  },
  validations() {
    const phoneValidator = (value) => {
      if (!value) return true;
      const numberInput = parsePhoneNumber(value, { regionCode: 'US' });
      return numberInput.valid;
    };

    return {
      username: { 
        required,
        emailOrPhone: or(email, phoneValidator)
      }
    }
  },
  beforeMount() {
    this.selectedTab = this.type;
    this.username = this.value;
  },
  mounted() {
    // The current version of the tab component uses <button> elements for the tabs.
    // By default buttons have a type of 'submit', which can cause HTML forms to be submitted if you
    // Put this component on a standard HTML form. This workaround will change the type to 'button'
    // So that the form is not submitted.
    // TODO: Once https://github.com/apptegy/components/pull/665/files is merged, we can remove this
    this.$refs.usernameSelector.querySelectorAll('button').forEach(button => {
      button.setAttribute('type', 'button');
    });
  },
  computed: {
    src() {
      return new URL('../../../assets/images/us-flag.png', import.meta.url).href;
    },
    isPossibleEmail() {
      return this.username && /([a-zA-Z]|@)/.test(this.username);
    },
  },
  methods: {
    onSwitchTab(event) {
      this.selectedTab = event.target.value;
      this.username = '';
      this.$emit('update:modelValue', '');
      this.$emit('type-changed', this.selectedTab);
    },
    onUsernameChange(newValue) {
      if (this.isPossibleEmail) {
        this.numberInput = { valid: false };
        this.$emit('update:modelValue', newValue);
      } else {
        this.numberInput = parsePhoneNumber(newValue, { regionCode: 'US' });
        this.$emit('update:modelValue', this.numberInput.valid ? this.numberInput.number.e164 : newValue);
      }
    },
    onUsernameConfirmChange(newValue) {
      this.$emit('confirm-input', newValue);
    },
    enterEvent(event) {
      if(!this.numberInput.valid) {
        event.preventDefault();
        this.$emit('show-password', true);
      }
    }
  },
};
</script>

<style lang="scss">
.username-selector {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: stretch;
}

.username-tab-bar {
  align-self: center;
  margin-bottom: 1rem;
}

.input-username-phone {
  align-self: center;
}

.phone-container {
  display: flex;
  justify-content: center;
}

.email-confirm {
  margin-top: 1rem;
}

.icon-left {
  position: absolute;
  height: 100%;
  align-content: center;
  & img {
    width: 30px;
    margin: 0 5px;
  }
  & p {
    display: inline;
    position: relative;
    bottom: 0.5px;
  }
}
.a-input input.a-input--has-icon-left{
  padding-left: 4.5em !important;
}

.form-group__error-message {
  display: flex;
}
</style>
